<template>
  <div v-if="!shouldHide && showNodeFn">
    <keep-alive v-if="!!node">
      <component
        :is="node"
        :content="content"
        v-bind="isCustom ? { kind, nodeData } : { kind, ...nodeData }"
        v-on="$listeners"
        :disabled="isFinished"
      />
    </keep-alive>
    <div v-else>node not found</div>
  </div>
</template>

<script>
import { Component } from 'vue'
import { PropsDefinition, Computed } from '@/_typedefs'
import BoxNodeVue from './Box/BoxNode.vue'
import ColumnNodeVue from './Column/ColumnNode.vue'
import ExpandableBoxNodeVue from './ExpandableBox/ExpandableBoxNode.vue'
import RowNodeVue from './Row/RowNode.vue'
import TitleNodeVue from './Title/TitleNode.vue'
import QuestionNodeVue from './Question/QuestionNode.vue'
import { createNamespacedHelpers } from 'vuex'
import ParentQuestionNodeVue from './Question/ParentQuestionNode.vue'
import GridNodeVue from './Grid/GridNode.vue'
import ParameterNodeVue from './Parameters/ParameterNode.vue'
import AsthmaFormNodeVue from '../Asthma/FormNodes/AsthmaFormNode.vue'
import ComputedQuestionNodeVue from './Question/ComputedQuestionNode.vue'
import ParametersWrapperNodeVue from './Parameters/ParametersWrapperNode.vue'
import AtopicDermatitisFormNode from '@/components/NewAttendanceForm/AtopicDermatitis/AtopicDermatitisFormNode.vue'
import ShareImageNodeVue from './ShareImageNode.vue'
import QrCodeNodeVue from './QrCodeNode.vue'
import FileUploadNodeVue from './FileUploadNode.vue'

const { mapGetters: newAttendanceFormGetters } = createNamespacedHelpers('newAttendanceForm')

/**
 * @typedef {Object} FormNodeProps
 * @property {string} kind
 * @property {"simplified" | "complete"} hideOn
 * @property {Record<string, any>} nodeData
 * @property {FormNodeProps | FormNodeProps[]} content
 */

export default {
  name: 'FormNode',
  /** @type {PropsDefinition<FormNodeProps>} */
  props: {
    kind: {
      type: String,
      required: true,
    },
    hideOn: {
      type: String,
      default: null,
    },
    nodeData: {
      type: Object,
      default: () => ({}),
    },
    content: {
      type: [Array, Object],
      default: null,
    },
    condition: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isCustom: this.kind.includes(':'),
    }
  },
  computed: {
    .../** @type {{simplified: Computed<boolean>}} */ (newAttendanceFormGetters(['simplified', 'attendance', 'isFinished'])),
    shouldHide() {
      switch (this.hideOn) {
        case 'simplified':
          return this.simplified
        case 'complete':
          return !this.simplified
        default:
          return false
      }
    },
    /** @type {Computed<Component | null>} */
    node() {
      const [node, disease] = this.kind.split(':').reverse()
      if (disease) {
        const diseaseMap = {
          asthma: AsthmaFormNodeVue,
          'atopic-dermatitis': AtopicDermatitisFormNode,
        }

        return diseaseMap[disease] ?? null
      }

      const nodeMap = {
        box: BoxNodeVue,
        column: ColumnNodeVue,
        'expandable-box': ExpandableBoxNodeVue,
        row: RowNodeVue,
        grid: GridNodeVue,
        title: TitleNodeVue,
        question: QuestionNodeVue,
        'parent-question': ParentQuestionNodeVue,
        'computed-question': ComputedQuestionNodeVue,
        parameter: ParameterNodeVue,
        'parameters-wrapper': ParametersWrapperNodeVue,
        'share-image': ShareImageNodeVue,
        'qr-code': QrCodeNodeVue,
        'file-upload': FileUploadNodeVue,
      }

      return nodeMap[node] ?? null
    },
    isPatientMale() {
      return this.attendance.patient.assignedSex === 'M'
    },
    showNodeFn() {
      const map = {
        isPatientMale: this.isPatientMale,
        isPatientFemale: !this.isPatientMale,
      }
      const fn = map[this.nodeData.showNodeFn]
      return fn ?? true
    },
  },
}
</script>

<style></style>
